import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * Advice modal shown after add-to-cart on Grind/Split product pages.
 *
 * @extends {DialogComponent}
 */
class GrindSplitCartModal extends DialogComponent {
  /** @type {HTMLElement | null} */
  #triggerElement = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { capture: true });
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { capture: true });
    this.removeEventListener('click', this.#handleClick);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const actionTarget = event.target instanceof Element ? event.target.closest('[data-grind-split-action]') : null;
    if (!actionTarget || !this.contains(actionTarget)) return;

    switch (actionTarget.getAttribute('data-grind-split-action')) {
      case 'close':
        event.preventDefault();
        this.closeDialog();
        break;
      case 'continue-shopping':
        event.preventDefault();
        this.handleContinueShopping();
        break;
      case 'go-to-cart':
        event.preventDefault();
        this.handleGoToCart();
        break;
    }
  };

  /**
   * Intercepts cart updates to show this modal instead of the cart drawer.
   *
   * @param {CustomEvent} event
   */
  #handleCartUpdate = (event) => {
    if (!document.getElementById('grind-split-cart-modal')) return;
    if (event.detail?.data?.didError) return;
    if (event.detail?.data?.source !== 'product-form-component') return;

    const productForm = event.target instanceof HTMLElement ? event.target : null;
    if (!productForm || productForm.tagName !== 'PRODUCT-FORM-COMPONENT') return;

    const addToCartButton =
      productForm.querySelector('add-to-cart-component button[name="add"]') ??
      productForm.querySelector('button[name="add"]');

    this.#triggerElement =
      addToCartButton instanceof HTMLElement ? addToCartButton : document.activeElement;

    const drawer = document.getElementById('header-cart-drawer');
    const hadAutoOpen = drawer?.hasAttribute('auto-open');
    if (hadAutoOpen) drawer.removeAttribute('auto-open');

    queueMicrotask(() => {
      if (hadAutoOpen) drawer?.setAttribute('auto-open', '');
      this.#openWithFocus();
    });
  };

  #openWithFocus() {
    this.showDialog();

    requestAnimationFrame(() => {
      this.refs.closeButton?.focus();
    });
  }

  closeDialog = async () => {
    const trigger = this.#triggerElement;
    const { dialog } = this.refs;

    if (!dialog?.open) return;

    dialog.classList.add('dialog-closing');

    document.body.style.width = '';
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: parseInt(scrollY) * -1, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());

    if (trigger instanceof HTMLElement) {
      trigger.focus();
    }
  };

  handleContinueShopping = () => {
    this.closeDialog();
  };

  handleGoToCart = async () => {
    await this.closeDialog();
    const drawer = document.getElementById('header-cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  };
}

if (!customElements.get('grind-split-cart-modal')) {
  customElements.define('grind-split-cart-modal', GrindSplitCartModal);
}
